// Client-Side Router for Pops Culture
import { isAuthenticated, getCurrentProfile } from './services/authService';
import { getCachedTermsConfig, hasAcceptedCurrentTerms } from './services/termsService';

export type RoutePath = '/' | '/app' | '/terms' | '/admin';

type RouteChangeHandler = (path: RoutePath, params?: Record<string, string>) => void;
const routeListeners: RouteChangeHandler[] = [];

export function getCurrentRoute(): RoutePath {
  const path = window.location.pathname;
  if (path.startsWith('/admin')) return '/admin';
  if (path.startsWith('/app')) return '/app';
  if (path.startsWith('/terms')) return '/terms';
  return '/';
}

export function navigateTo(path: RoutePath, replace: boolean = false): void {
  // Authentication gate for /app
  if (path === '/app' && !isAuthenticated()) {
    console.log('[Pops Culture Router] Unauthenticated visit to /app -> redirecting to /');
    if (replace) {
      window.history.replaceState({}, '', '/');
    } else {
      window.history.pushState({}, '', '/');
    }
    dispatchRoute('/');
    return;
  }

  // Terms acceptance gate for authenticated users
  if (path !== '/app' && isAuthenticated()) {
    const profile = getCurrentProfile();
    const currentTerms = getCachedTermsConfig();
    if (profile && !hasAcceptedCurrentTerms(profile, currentTerms.version)) {
      console.warn('[Pops Culture Router] Latest terms not accepted -> forcing redirect to /app');
      if (replace) {
        window.history.replaceState({}, '', '/app');
      } else {
        window.history.pushState({}, '', '/app');
      }
      dispatchRoute('/app');
      return;
    }
  }

  // Admin gate for /admin (strictly coryliseno@gmail.com)
  if (path === '/admin') {
    const profile = getCurrentProfile();
    const isAdminUser = profile && profile.email && profile.email.toLowerCase().trim() === 'coryliseno@gmail.com';
    if (!isAdminUser) {
      console.warn('[Pops Culture Router] Unauthorized access attempt to /admin -> redirecting to /');
      if (replace) {
        window.history.replaceState({}, '', '/');
      } else {
        window.history.pushState({}, '', '/');
      }
      dispatchRoute('/');
      return;
    }
  }

  if (replace) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
  }
  dispatchRoute(path);
}

function dispatchRoute(path: RoutePath): void {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  routeListeners.forEach(listener => listener(path));
}

export function subscribeRoute(listener: RouteChangeHandler): () => void {
  routeListeners.push(listener);
  listener(getCurrentRoute());
  return () => {
    const idx = routeListeners.indexOf(listener);
    if (idx !== -1) routeListeners.splice(idx, 1);
  };
}

// Handle browser Back / Forward buttons
window.addEventListener('popstate', () => {
  dispatchRoute(getCurrentRoute());
});
